import { useState } from 'react';
import type { CustomerDetail, CustomerUser } from '@shared/types';
import { useAuth } from '../app/auth';
import { api, describeError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { formatDateTime } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Input,
  Modal,
  Notice,
  PageHeader,
  Pagination,
  Select,
  Table,
  type Column,
} from '../components/ui';

interface CustomerList {
  items: CustomerUser[];
  total: number;
  page: number;
  pageSize: number;
}

export function CustomersPage() {
  const { can } = useAuth();
  const canManage = can('customer.manage');

  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [verified, setVerified] = useState('');
  const [query, setQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [version, setVersion] = useState(0);

  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const customers = useAsync<CustomerList>(() => {
    const params = new URLSearchParams({ page: String(page), pageSize: '20' });
    if (status !== '') params.set('status', status);
    if (verified !== '') params.set('emailVerified', verified);
    if (appliedQuery !== '') params.set('query', appliedQuery);
    return api.get(`/api/customers?${params.toString()}`);
  }, [page, status, verified, appliedQuery, version]);

  async function openDetail(user: CustomerUser) {
    setLoadingId(user.id);
    setError(null);
    setNotice(null);
    try {
      setDetail(await api.get<CustomerDetail>(`/api/customers/${user.id}`));
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setLoadingId(null);
    }
  }

  async function act(id: string, action: 'status' | 'sessions' | 'reset', body?: unknown) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (action === 'status') {
        await api.post(`/api/customers/${id}/status`, body);
        setNotice('账号状态已更新');
      } else if (action === 'sessions') {
        await api.post(`/api/customers/${id}/sessions/revoke`);
        setNotice('该账号的全部会话已被踢出');
      } else {
        const result = await api.post<{ sent: boolean; resetUrl: string | null }>(
          `/api/customers/${id}/password-reset`,
        );
        setNotice(
          result.sent
            ? '改密邮件已发送'
            : `邮件发送失败，请人工转达链接：${result.resetUrl ?? ''}`,
        );
      }
      setVersion((value) => value + 1);
      const refreshed = await api.get<CustomerDetail>(`/api/customers/${id}`);
      setDetail(refreshed);
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setBusy(false);
    }
  }

  const columns: Column<CustomerUser>[] = [
    {
      key: 'email',
      header: '账号',
      render: (row) => (
        <div>
          <p className="text-fg">{row.email}</p>
          <p className="text-xs text-fg-subtle">
            {row.name}
            {row.phone ? ` · ${row.phone}` : ''}
          </p>
        </div>
      ),
    },
    {
      key: 'verified',
      header: '邮箱',
      render: (row) =>
        row.emailVerified ? <Badge tone="ok">已验证</Badge> : <Badge tone="warn">未验证</Badge>,
    },
    {
      key: 'status',
      header: '状态',
      render: (row) =>
        row.status === 'active' ? <Badge tone="ok">正常</Badge> : <Badge tone="danger">已停用</Badge>,
    },
    {
      key: 'tenants',
      header: '组织数',
      render: (row) => <span className="tabular">{row.tenantCount}</span>,
    },
    {
      key: 'created',
      header: '注册时间',
      render: (row) => <span className="text-xs text-fg-muted">{formatDateTime(row.createdAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      width: '90px',
      render: (row) => (
        <Button size="sm" loading={loadingId === row.id} onClick={() => void openDetail(row)}>
          详情
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="客户"
        description="个人与企业客户共用同一套账号模型；停用会立刻踢出全部会话。"
      />

      {error && detail === null ? <Notice tone="danger">{error}</Notice> : null}

      <Card>
        <CardHeader title="账号列表" hint={`${customers.data?.total ?? 0} 个账号`} />
        <div className="mb-3 flex flex-wrap gap-2">
          <div className="w-36">
            <Select
              value={status}
              onChange={(value) => {
                setStatus(value);
                setPage(1);
              }}
              options={[
                { value: '', label: '全部状态' },
                { value: 'active', label: '正常' },
                { value: 'suspended', label: '已停用' },
              ]}
            />
          </div>
          <div className="w-36">
            <Select
              value={verified}
              onChange={(value) => {
                setVerified(value);
                setPage(1);
              }}
              options={[
                { value: '', label: '全部邮箱' },
                { value: 'true', label: '已验证' },
                { value: 'false', label: '未验证' },
              ]}
            />
          </div>
          <div className="w-64">
            <Input
              value={query}
              onChange={setQuery}
              placeholder="邮箱 / 姓名 / 用户 ID"
              onEnter={() => {
                setAppliedQuery(query.trim());
                setPage(1);
              }}
            />
          </div>
          <Button
            onClick={() => {
              setAppliedQuery(query.trim());
              setPage(1);
            }}
          >
            搜索
          </Button>
        </div>

        <Table
          columns={columns}
          rows={customers.data?.items ?? []}
          getKey={(row) => row.id}
          loading={customers.loading}
          empty="没有符合条件的客户"
        />
        <Pagination
          page={page}
          pageSize={customers.data?.pageSize ?? 20}
          total={customers.data?.total ?? 0}
          onChange={setPage}
        />
      </Card>

      <Modal
        open={detail !== null}
        title="客户详情"
        onClose={() => setDetail(null)}
        width="max-w-2xl"
        footer={<Button onClick={() => setDetail(null)}>关闭</Button>}
      >
        {detail ? (
          <div className="space-y-4">
            {error ? <Notice tone="danger">{error}</Notice> : null}
            {notice ? <Notice tone="ok">{notice}</Notice> : null}

            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
              <div>
                <dt className="text-fg-muted">邮箱</dt>
                <dd className="text-fg">{detail.user.email}</dd>
              </div>
              <div>
                <dt className="text-fg-muted">姓名</dt>
                <dd className="text-fg">{detail.user.name}</dd>
              </div>
              <div>
                <dt className="text-fg-muted">状态</dt>
                <dd className="text-fg">{detail.user.status}</dd>
              </div>
              <div>
                <dt className="text-fg-muted">邮箱验证</dt>
                <dd className="text-fg">{detail.user.emailVerified ? '已验证' : '未验证'}</dd>
              </div>
              <div>
                <dt className="text-fg-muted">注册来源</dt>
                <dd className="text-fg">{detail.registrationSource ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-fg-muted">注册 IP</dt>
                <dd className="text-fg">{detail.registrationIp ?? '—'}</dd>
              </div>
            </dl>

            <div>
              <p className="mb-1 text-[13px] font-medium text-fg">所在组织</p>
              {detail.memberships.length === 0 ? (
                <p className="text-xs text-fg-muted">没有成员关系</p>
              ) : (
                <ul className="space-y-1 text-[13px]">
                  {detail.memberships.map((membership) => (
                    <li
                      key={membership.tenantId}
                      className="flex flex-wrap items-center gap-2 rounded-md bg-canvas px-3 py-2"
                    >
                      <span className="text-fg">{membership.tenantName}</span>
                      <Badge>{membership.role}</Badge>
                      <Badge tone={membership.tenantKind === 'enterprise' ? 'brand' : 'neutral'}>
                        {membership.tenantKind === 'enterprise' ? '企业' : '个人'}
                      </Badge>
                      <Badge
                        tone={
                          membership.verificationStatus === 'verified'
                            ? 'ok'
                            : membership.verificationStatus === 'pending'
                              ? 'warn'
                              : 'danger'
                        }
                      >
                        {membership.verificationStatus}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {canManage ? (
              <div className="flex flex-wrap gap-2 border-t border-line pt-3">
                <Button
                  loading={busy}
                  onClick={() =>
                    void act(detail.user.id, 'status', {
                      status: detail.user.status === 'active' ? 'suspended' : 'active',
                    })
                  }
                >
                  {detail.user.status === 'active' ? '停用账号' : '启用账号'}
                </Button>
                <Button loading={busy} onClick={() => void act(detail.user.id, 'sessions')}>
                  踢出全部会话
                </Button>
                <Button loading={busy} onClick={() => void act(detail.user.id, 'reset')}>
                  发送改密链接
                </Button>
              </div>
            ) : (
              <Notice tone="info">当前角色只能查看，不能修改客户状态。</Notice>
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
